import _ from 'underscore';

import BenchlingConfig from 'BenchlingConfig';
import {LegacyMeasurement} from 'constants/MeasurementConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {
  AccessibleGridCell,
  ContainableSummary,
  TRANSFER_MODAL_PANES,
  ValidationErrors,
} from 'constants/SampleConstants';
import {BioentitySummary} from 'folder-items/FileConstants';
import {canProceedWithSamples} from 'inventory/sample-status/SampleStatusWarning';
import NavStore from 'nav/NavStore';
import {CreateContainersPaneState} from 'samples/containers/CreateContainerPane';
import {CreateBoxPaneState} from 'samples/CreateBoxPane';
import {CreateLocationPaneState} from 'samples/CreateLocationPane';
import {CreatePlatePaneState} from 'samples/CreatePlatePane';
import {ExistingPlatePaneState} from 'samples/ExistingPlatePane';
import {TYPE as InputSampleType} from 'samples/transfers/input-sample/InputSampleConstants';
import * as TransferInputPaneStateUtil from 'samples/transfers/input-sample/PaneStateUtil';
import {
  ConfirmConcentrationsData,
  ConfirmConcentrationsPaneState,
  ConfirmTransferQuantitiesPaneState,
  ContainerSummaryWithContainedItemIds,
  MaybeNotYetCreatedContainer,
  OutputSampleType,
  StorableModalsContainerContent,
  StorableModalsContainerTransfer,
  TransferModalState,
  TransferPaneSampleStatusState,
} from 'samples/transfers/TransferModalUtil';
import {
  addMeasurementValues,
  getMeasurementError,
  getQuantitiesForTransferValidation,
  getTransferQuantitiesFromConfirmTransferQuantitiesData,
  isNonVolumeTransfer,
  isSourceContainerValidForTransfer,
  isValidLegacyMeasurement,
  validateContainerTransfer,
} from 'samples/transfers/TransferUtil';
import {hasValueForAllRequiredFields} from 'schemas/tags/TagUtil';
import {Optional} from 'util/Types';

import * as SampleUtil from './SampleUtil';

interface ExcludedFields {
  parentStorable?: boolean;
  project?: boolean;
}

type CreateStorablePaneState =
  | CreateContainersPaneState
  | CreateBoxPaneState
  | CreatePlatePaneState
  | CreateLocationPaneState;

// See SampleConstants.SelectGridMode
export const SELECT_GRID_MODES = {EXISTING: 'EXISTING', CREATE: 'CREATE', CHOOSE: 'CHOOSE'};

export function isSourceComplete(sourceId: string | null): boolean {
  if (BenchlingConfig.ENABLE_PROJECTS_REQUIRED_FOR_NON_LOCATION_STORABLES) {
    return (
      sourceId != null &&
      NavStore.getProjects()
        .map((project) => project.id)
        .includes(sourceId)
    );
  }
  return true;
}

export function isBioentitySummary(
  containableSummary?: ContainableSummary | null
): containableSummary is BioentitySummary {
  return Boolean(containableSummary?.hasOwnProperty('tagSchema'));
}

/**
 * @package exported for testing
 */
export function _getCreateStorableValidationErrors(
  createPaneState: CreateStorablePaneState,
  excludedFields: ExcludedFields = {},
  numToCreate?: number
): ValidationErrors {
  // excludedFields can include project or parentStorable to not require those fields
  numToCreate = numToCreate || createPaneState.numToCreate || 1;
  const {customBarcode, selectedParent, selectedSchema, shouldAutogenerateBarcode} = createPaneState;
  const errors: ValidationErrors = {};
  if (!selectedSchema) {
    errors.schemaError = 'Please select a schema.';
  } else if (
    !excludedFields.parentStorable &&
    BenchlingConfig.ENABLE_REQUIRED_PARENT_STORABLE &&
    !selectedParent.storable
  ) {
    errors.parentStorableError = 'Please specify a location.';
  }
  if (
    !shouldAutogenerateBarcode &&
    (!customBarcode || (numToCreate > 1 && customBarcode.split(/[\s,]+/).length !== numToCreate))
  ) {
    if (numToCreate > 1) {
      errors.barcodeError = `Please specify ${numToCreate} comma-separated barcodes.`;
    } else {
      errors.barcodeError = 'Please specify a barcode.';
    }
  }
  if (!excludedFields.project && !isSourceComplete(createPaneState.sourceId)) {
    errors.projectError = 'Please select a project.';
  }
  if (selectedSchema && !hasValueForAllRequiredFields(createPaneState.tags, selectedSchema.fields)) {
    errors.tagsError = 'Please fill in all required fields.';
  }
  return errors;
}

export function isExistingPlatePaneComplete(paneState: Partial<ExistingPlatePaneState>): boolean {
  return Boolean(paneState.selectedGrid);
}

function areSampleStatusWarningsBlocking(
  sampleStatusState: Optional<TransferPaneSampleStatusState>,
  inputOutputType: Optional<InputSampleType | OutputSampleType>
): boolean {
  // If warning status is set within a specific pane, its input / output type must match to avoid blocking
  // proceeding with another type (e.g. a non-overridden input container warning should not block using an
  // input entity.
  const warningIsForSelectedPane =
    sampleStatusState?.warningInputOutputType == null ||
    sampleStatusState.warningInputOutputType === inputOutputType;
  return (
    sampleStatusState != null &&
    !canProceedWithSamples(
      sampleStatusState.selectedSampleIds,
      sampleStatusState.shouldShowStatusWarnings,
      sampleStatusState.canProceedOnSampleStatus
    ) &&
    warningIsForSelectedPane
  );
}

function areTransferInputContentsValid(
  inputContainer?: ContainerSummaryWithContainedItemIds | null,
  inputType: InputSampleType = InputSampleType.EXISTING_CONTAINER
): boolean {
  return inputType === InputSampleType.EXISTING_CONTAINER && inputContainer != null
    ? isSourceContainerValidForTransfer(inputContainer)
    : true;
}

export function isChooseTransferInputPaneComplete(paneState: TransferModalState): boolean {
  return (
    TransferInputPaneStateUtil.isComplete(paneState) &&
    areTransferInputContentsValid(paneState.inputContainer, paneState.inputType) &&
    !areSampleStatusWarningsBlocking(paneState.inputSampleStatusState, paneState.inputType) &&
    // Check for output warnings shown on input pane
    !areSampleStatusWarningsBlocking(paneState.outputSampleStatusState, null)
  );
}

export function isChooseTransferOutputPaneComplete(paneState: Partial<TransferModalState>): boolean {
  const validOutputSelected =
    Boolean(
      paneState.outputType === ModelTypeConstants.CONTAINER && paneState.outputContainerSummaries?.length
    ) ||
    Boolean(
      paneState.outputType === ModelTypeConstants.PLATE &&
      paneState.outputPlate &&
      paneState.outputCells?.length
    );
  return (
    validOutputSelected &&
    areTransferInputContentsValid(paneState.selectedInputContainer) &&
    !areSampleStatusWarningsBlocking(paneState.outputSampleStatusState, paneState.outputType) &&
    // Check for input warnings shown on output pane
    !areSampleStatusWarningsBlocking(paneState.inputSampleStatusState, null)
  );
}

export function isConfirmTransferQuantitiesPaneComplete(
  paneState: Partial<ConfirmTransferQuantitiesPaneState>
): boolean {
  if (!paneState.confirmTransferQuantitiesData || !paneState.confirmTransferQuantitiesData.length) {
    return false;
  }
  // finalOutputQuantityValue is not required if transferring into a container with null quantity
  if (paneState.inputContainable) {
    return (
      isValidLegacyMeasurement(paneState.inputContainableConcentration) &&
      // eslint-disable-next-line no-restricted-syntax
      _.every(
        paneState.confirmTransferQuantitiesData,
        (data) =>
          data.outputContainer &&
          isValidLegacyMeasurement({
            value: addMeasurementValues(data.outputContainer.quantity.value, data.transferQuantityValue),
            units: data.outputContainer.quantity.units,
          })
      )
    );
  } else {
    const {isValid: transferQuantityIsValid, quantityErrorsAreBlocking} = !paneState.selectedInputContainer
      ? {isValid: true, quantityErrorsAreBlocking: false}
      : getQuantitiesForTransferValidation(
          paneState.selectedInputContainer.quantity,
          getTransferQuantitiesFromConfirmTransferQuantitiesData(paneState.confirmTransferQuantitiesData)
        );
    return (
      (!quantityErrorsAreBlocking || transferQuantityIsValid) &&
      paneState.confirmTransferQuantitiesData.every((data) => validateContainerTransfer(data))
    );
  }
}

export function isConfirmConcentrationsPaneComplete(paneState: Partial<TransferModalState>): boolean {
  const {errorMessage, isTransferring, containerContents} = paneState;
  return (
    !errorMessage &&
    !isTransferring &&
    containerContents != null &&
    containerContents.every(({concentration}) => isValidLegacyMeasurement(concentration))
  );
}

export function isConfirmSampleControlsPaneComplete(paneState: Partial<TransferModalState>): boolean {
  const {isTransferring} = paneState;
  return !isTransferring;
}

// See SampleConstants.ValidationErrors
export function getCreateContainerValidationErrors(
  createPaneState: CreateContainersPaneState
): ValidationErrors {
  const {concentration, inputContainable, selectedParent, quantity} = createPaneState;

  const {numToCreate = 0} = createPaneState;

  const errors = _getCreateStorableValidationErrors(createPaneState, {}, numToCreate);

  if (!numToCreate || numToCreate < 1) {
    errors.numToCreateError = 'Please create at least one container.';
  }

  if (isBioentitySummary(inputContainable) && inputContainable.tagSchema != null) {
    if (!inputContainable.tagSchema.isContainable) {
      errors.contentsError =
        "Entity isn't permitted to be transferred into containers based on your organization's registry " +
        'settings.';
    }
  }

  const concentrationError = getMeasurementError(concentration);
  if (concentrationError != null) {
    errors.concentrationError = concentrationError;
  }
  const quantityError = getMeasurementError(quantity);
  if (quantityError != null) {
    errors.quantityError = quantityError;
  }

  if (selectedParent.storable && SampleUtil.isGridId(selectedParent.storable.id)) {
    if (selectedParent.gridCells.length < numToCreate) {
      errors.parentStorableGridError = 'Please select a position for every container being created.';
    } else if (selectedParent.gridCells.length === 0) {
      errors.parentStorableGridError = 'Please select at least one position.';
    } else if (selectedParent.gridCells.length > numToCreate) {
      errors.parentStorableGridError =
        'Please update number of containers to match the number of positions selected.';
    }
  }
  return errors;
}

// See SampleConstants.ValidationErrors
export function getCreateBoxValidationErrors(createPaneState: CreateBoxPaneState): ValidationErrors {
  return _getCreateStorableValidationErrors(createPaneState);
}

// See SampleConstants.ValidationErrors
export function getCreatePlateValidationErrors(createPaneState: CreatePlatePaneState): ValidationErrors {
  const errors = _getCreateStorableValidationErrors(createPaneState);
  const {selectedSchema, selectedContainerSchema} = createPaneState;
  if (SampleUtil.isMatrixPlateSchema(selectedSchema) && !selectedContainerSchema) {
    errors.containerSchemaError = 'Please select a schema.';
  }
  return errors;
}

// See SampleConstants.ValidationErrors
export function getCreateLocationValidationErrors(
  createPaneState: CreateLocationPaneState
): ValidationErrors {
  const excludedFields: ExcludedFields = {
    parentStorable: true,
    project: true,
  };
  const errors = _getCreateStorableValidationErrors(createPaneState, excludedFields);
  // Block creation if capacity error is present. Displaying error is handled by the capacity input.
  if (createPaneState.hasCapacityError) {
    errors.hasLocationCapacityError = true;
  }
  return errors;
}

export function isFirstTransferPane(pane: string): boolean {
  const paneIndex = TRANSFER_MODAL_PANES.indexOf(pane);
  return paneIndex === 0;
}

export function isLastTransferPane(pane: string): boolean {
  const paneIndex = TRANSFER_MODAL_PANES.indexOf(pane);
  return paneIndex === TRANSFER_MODAL_PANES.length - 1;
}

export function isSpreadsheetLoading(paneState: Partial<CreatePlatePaneState>): boolean {
  return paneState.spreadsheetFile != null && !paneState.spreadsheetURL;
}

export function transferParamsFromCellsToNewPlate(cells: Array<AccessibleGridCell>): {
  containerTransfers: Array<StorableModalsContainerTransfer>;
  containerContents: Array<StorableModalsContainerContent>;
} {
  const containerTransfers: Array<StorableModalsContainerTransfer> = [];
  const containerContents: Array<StorableModalsContainerContent> = [];
  cells.forEach((cell, index) => {
    const gridCellCoordinates = SampleUtil.formatGridCoordinates(cell.rowIndex, cell.columnIndex);
    const outputContainerToCreate: MaybeNotYetCreatedContainer = {
      id: index.toString(),
      name: `_not_yet_created_${gridCellCoordinates}`, // placeholder value for TS, will never be used
      gridCellCoordinates,
      quantity: {value: null, units: null},
    };

    containerTransfers.push({
      inputSampleId: cell.container.id,
      outputContainer: outputContainerToCreate,
      quantity: cell.container.quantity,
    });

    cell.container.contents.forEach((content) => {
      containerContents.push({
        concentration: content.concentration,
        containedItemId: content.containedItemId,
        containerId: outputContainerToCreate.id,
        container: outputContainerToCreate,
      });
    });
  });
  return {containerTransfers, containerContents};
}

function _containerLabel(container: MaybeNotYetCreatedContainer): string {
  return container.gridCellCoordinates || container.name;
}

function getConfirmConcentrationsData(
  containerTransfers: Array<StorableModalsContainerTransfer> | null,
  containerContents: Array<StorableModalsContainerContent> | null
): ConfirmConcentrationsData | null {
  /*
    Reshape containerTransfers and containerContents into an array of {
      label,
      containerContents, an array of {containedItemId, container, concentration}, sorted by containedItemId
    }, sorted by label

    Expects each container to have a unique label. Thus this does not support:
    - Listing output containers across multiple grids (where the grid coordinates will conflict)
  */
  if (!containerTransfers || !containerContents) {
    return null;
  }
  const containerContentsByContainer: {[label: string]: Array<StorableModalsContainerContent>} = {};
  // The set of `container` in containerContents is a subset of the set of `outputContainer` in
  // containerTransfers, excluding containers with no batches in them (which we still want to display).
  containerTransfers.forEach(({outputContainer}) => {
    containerContentsByContainer[_containerLabel(outputContainer)] = [];
  });

  containerContents.forEach((content) => {
    containerContentsByContainer[_containerLabel(content.container)].push(content);
  });

  // eslint-disable-next-line no-restricted-syntax
  const confirmConcentrationsData: ConfirmConcentrationsData = _.map(
    containerContentsByContainer,
    (containerContents, label) => ({
      label,
      containerContents: _.sortBy(containerContents, 'containedItemId'),
    })
  );

  return _.sortBy(confirmConcentrationsData, 'label');
}

function getAreSolidUnitsInvolved(
  containerTransfers: Array<StorableModalsContainerTransfer> | null,
  inputContainer: {quantity: LegacyMeasurement} | null
): boolean {
  return Boolean(containerTransfers?.some((transfer) => isNonVolumeTransfer(transfer, inputContainer)));
}

export function getShouldCalculateConcentrations(
  shouldCalculateConcentrations: boolean,
  containerTransfers: Array<StorableModalsContainerTransfer> | null,
  inputContainer: {quantity: LegacyMeasurement} | null
): boolean {
  // We do not support concentration calculations for solid transfers at this time
  return !getAreSolidUnitsInvolved(containerTransfers, inputContainer) && shouldCalculateConcentrations;
}

export function getUpdatedConcentrationPaneState({
  containerTransfers,
  containerContents,
  shouldCalculateConcentrations,
  inputContainer,
  errorMessage,
}: {
  containerTransfers: Array<StorableModalsContainerTransfer> | null;
  containerContents: Array<StorableModalsContainerContent> | null;
  shouldCalculateConcentrations: boolean;
  inputContainer: {quantity: LegacyMeasurement} | null;
  errorMessage: string | null;
}): ConfirmConcentrationsPaneState {
  return {
    confirmConcentrationsData: getConfirmConcentrationsData(containerTransfers, containerContents),
    errorMessage,
    areSolidUnitsInvolved: getAreSolidUnitsInvolved(containerTransfers, inputContainer),
    shouldCalculateConcentrations: getShouldCalculateConcentrations(
      shouldCalculateConcentrations,
      containerTransfers,
      inputContainer
    ),
  };
}
